import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ListCard from "../components/ListCard";
import {
  includesQuery,
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import {
  CONTROL_VISIBILITY,
  DEFAULT_CONTROL_VISIBILITY,
  controlsOfShownFrameworks,
  isHiddenControl,
  visibleControls,
} from "../../data/controls";
import {
  DialogHeader,
  dialogActionsSx,
  dialogPaperProps,
} from "../../components/DialogParts";

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// "Which controls do visitors see?" One choice for the whole Trust Center.
const VisibilityDialog = ({ open, value, onClose, onSave }) => {
  const theme = useTheme();
  const [choice, setChoice] = useState(value);

  useEffect(() => {
    if (open) setChoice(value);
  }, [open, value]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={dialogPaperProps}
    >
      <DialogHeader
        title="Control visibility"
        subtitle="Choose which controls visitors see, and whether they see each control's status."
        onClose={onClose}
      />
      <DialogContent dividers>
        <RadioGroup
          aria-label="Control visibility"
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
        >
          {Object.entries(CONTROL_VISIBILITY).map(([key, option]) => (
            <FormControlLabel
              key={key}
              value={key}
              control={<Radio />}
              sx={{ alignItems: "flex-start", mb: 1.5, mr: 0 }}
              label={
                <Box sx={{ pt: 1 }}>
                  <Typography sx={{ fontWeight: 500 }}>
                    {option.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.customColors.text.secondary }}
                  >
                    {option.description}
                  </Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={dialogActionsSx(theme)}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={outlinedButtonSx(theme)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={choice === value}
          onClick={() => {
            onSave(choice);
            onClose();
          }}
          sx={primaryButtonSx(theme)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Status as in Checks: passing, failing, or no check mapped yet.
const STATUS_CHIP = {
  pass: { label: "Passing", tone: "success" },
  fail: { label: "Failing", tone: "error" },
  unmapped: { label: "No checks", tone: null },
};

// Filter pills (FilterNSearchBar takes labels).
const ON_PAGE = "On the page";
const LEFT_OFF = "Left off";
const SHOWN_FILTERS = [ON_PAGE, LEFT_OFF];

const columns = [
  { label: "Control", field: "name", width: "46%" },
  { label: "Domain", field: "domain", width: "24%" },
  { label: "Status", field: "status", width: "14%" },
  { label: "On the page", field: "shown", width: "16%", align: "right" },
];

// Controls tab: controls are live from Regodit and are not edited here. The
// admin chooses whether the page has a Controls section at all, which
// controls are on it (one by one), and how much visitors see of them.
// Only controls of the frameworks shown on the Compliance tab count:
// hiding a framework there hides its controls here and on the page.
const ControlsTab = ({
  controls,
  frameworks,
  visibility,
  // Off until the admin turns it on (the API's default too).
  controlsShown = false,
  hiddenControlIds = [],
  onChange,
}) => {
  const theme = useTheme();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(null);
  const mode = CONTROL_VISIBILITY[visibility]
    ? visibility
    : DEFAULT_CONTROL_VISIBILITY;
  const choices = { controlsShown, hiddenControlIds };

  const inScope = useMemo(
    () => controlsOfShownFrameworks(controls, frameworks),
    [controls, frameworks]
  );
  const shown = visibleControls(controls, mode, frameworks, choices);
  const shownFrameworks = (frameworks || []).filter((f) => f.isVisible);
  const count = (status) => inScope.filter((c) => c.status === status).length;
  const leftOff = inScope.filter((c) => isHiddenControl(c, choices)).length;
  const hidden = inScope.length - shown.length;

  const rows = inScope.filter(
    (c) =>
      (!filter || (filter === LEFT_OFF) === isHiddenControl(c, choices)) &&
      includesQuery(search, c.name, c.title, c.domain)
  );

  const setHidden = (control, hide) => {
    const id = Number(control.id);
    const rest = hiddenControlIds.filter((h) => h !== id);
    onChange(
      { hiddenControlIds: hide ? [...rest, id] : rest },
      hide
        ? `${control.name} is left off your page.`
        : `${control.name} is back on your page.`
    );
  };

  const renderCell = (row, column) => {
    switch (column.field) {
      case "name":
        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {row.name}
            </Typography>
            {row.title && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: theme.customColors.text.secondary,
                }}
              >
                {row.title}
              </Typography>
            )}
          </Box>
        );
      case "status": {
        const chip = STATUS_CHIP[row.status] || STATUS_CHIP.unmapped;
        return (
          <Chip
            size="small"
            label={chip.label}
            sx={
              chip.tone
                ? {
                    color: theme.customColors.status[chip.tone].text,
                    backgroundColor: theme.customColors.status[chip.tone].bg,
                  }
                : { backgroundColor: theme.customColors.background.light }
            }
          />
        );
      }
      case "shown":
        return (
          <Switch
            checked={!isHiddenControl(row, choices)}
            disabled={!controlsShown}
            onChange={(e) => setHidden(row, !e.target.checked)}
            inputProps={{ "aria-label": `Show ${row.name} on your page` }}
          />
        );
      default:
        return row[column.field] || "—";
    }
  };

  return (
    <Stack spacing={2}>
      <Card
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "12px",
          border: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          {/* The text takes the room left; the buttons keep their size. */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FormControlLabel
              sx={{ ml: -1.25, mr: 0, gap: 0.5 }}
              control={
                <Switch
                  checked={controlsShown}
                  onChange={(e) =>
                    onChange(
                      { controlsShown: e.target.checked },
                      e.target.checked
                        ? "Controls are shown on your page."
                        : "Controls are hidden from your page."
                    )
                  }
                />
              }
              label={
                <Typography sx={{ fontWeight: 600 }}>
                  Show controls on your Trust Center
                </Typography>
              }
            />
            {controlsShown ? (
              <>
                <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.75 }}>
                  {CONTROL_VISIBILITY[mode].label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.customColors.text.secondary }}
                >
                  {CONTROL_VISIBILITY[mode].description}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.customColors.text.secondary, mt: 1 }}
                >
                  Right now: {plural(count("pass"), "control")} passing,{" "}
                  {count("fail")} failing, {count("unmapped")} with no checks
                  mapped. Visitors see {shown.length} of {inScope.length}
                  {hidden > 0
                    ? ` (${hidden} hidden${leftOff > 0 ? `, ${leftOff} by you` : ""})`
                    : ""}
                  .
                </Typography>
                <Typography
                  variant="body2"
                  aria-label="Frameworks the controls come from"
                  sx={{ color: theme.customColors.text.secondary, mt: 0.5 }}
                >
                  {shownFrameworks.length > 0
                    ? `Controls of the frameworks shown on your page: ${shownFrameworks
                        .map((f) => f.name)
                        .join(
                          ", "
                        )}. Hide a framework on the Compliance tab to hide its controls.`
                    : "No framework is shown on your page, so visitors see no controls. Show a framework on the Compliance tab."}
                </Typography>
              </>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: theme.customColors.text.secondary, mt: 0.5 }}
              >
                Your page has no Controls tab and does not say how many controls
                Regodit monitors. Turn this on to choose which controls visitors
                see.
              </Typography>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            {/* Status is live: the list below has the refresh button. */}
            <Button
              variant="outlined"
              disabled={!controlsShown}
              startIcon={<TuneOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={() => setSettingsOpen(true)}
              sx={{
                ...outlinedButtonSx(theme),
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              Visibility settings
            </Button>
          </Stack>
        </Stack>
      </Card>

      <ListCard
        title="Controls"
        subtitle={
          controlsShown
            ? "Turn a control off to leave it off your page. Status comes live from Regodit; fix failing controls on the Controls page."
            : "Controls are hidden from your page. Turn on “Show controls on your Trust Center” to choose them."
        }
        refreshQueries={["getTrustCenter"]}
        searchQuery={search}
        onSearchChange={setSearch}
        filters={SHOWN_FILTERS}
        selectedFilter={filter}
        onFilterChange={setFilter}
        onClearFilters={() => {
          setSearch("");
          setFilter(null);
        }}
        countNoun="control"
        columns={columns}
        rows={rows}
        renderCell={renderCell}
        emptyText={
          shownFrameworks.length === 0
            ? "No framework is shown on your page, so there are no controls to choose."
            : "No controls match."
        }
      />

      <VisibilityDialog
        open={settingsOpen}
        value={mode}
        onClose={() => setSettingsOpen(false)}
        onSave={(next) =>
          onChange({ controlVisibility: next }, "Control visibility saved.")
        }
      />
    </Stack>
  );
};

export default ControlsTab;
